import {
  Container,
  Form,
  FullLoadingIndicator,
  getTextInputProps,
  InputField,
  InputItem,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { Badge, Button } from '@windmill/react-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  patchApplicationDetails,
  readApplication,
  updateApplicationLifecycle,
} from '../../common/api/application';
import {
  EXTERNAL_APP_HOSTING_LINKS,
  INTERNAL_APP_HOSTING_LINKS,
} from '../../common/constants';
import { AppStatusCommand, AppType } from '../../common/enums';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import { getApplicationSubmission } from '../../common/reducers/applicationSubmissionSlice';
import { getFormHostingLinksFromAppUrls } from '../../common/utilities/applicationUtitility';
import { AppUrlChoices } from './types';

interface AppPublishForm extends AppUrlChoices {
  listingName: string;
  urlPath: string;
  appType: string;
  shortDescription: string;
  longDescription: string;
  version: string;
  consoleCompatible: boolean;
  isMultiTenant: boolean;
  honorsCopyright: boolean;
}

const appHostingLinks = EXTERNAL_APP_HOSTING_LINKS.concat(
  INTERNAL_APP_HOSTING_LINKS
);
const DEFAULT_APP_ICON =
  'https://cdn.getos1.com/7adcf59e-c5df-418f-8645-e82f2a5231b6-default_icon.png';

export const ApplicationPublishSummary: React.FunctionComponent = () => {
  const { applicationId } = useParams();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();
  const [application, setApplication] = React.useState<ApplicationSubmission>();
  const [appUrlChoices, setAppUrlChoices] = React.useState<AppUrlChoices>({});
  const [isMultiTenant, setIsMultiTenant] = useState(false);
  const [honorsCopyright, setHonorsCopyright] = useState(false);
  const [iconUrl, setIconUrl] = useState<string>(DEFAULT_APP_ICON);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();

  const appSubmissionState = useSelector(getApplicationSubmission);

  useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      const getApplication = async () => {
        const app = await readApplication(
          currentOrg.id,
          currentTeam.id,
          applicationId
        );

        if (app.icon) {
          setIconUrl(app.icon.fileUrl);
        }
      };
      getApplication();
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<AppPublishForm>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return { ...appSubmissionState, ...appUrlChoices };
    }, [appSubmissionState, appUrlChoices]),
  });

  useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      setApplication(appSubmissionState);
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  useEffect(() => {
    if (!application) {
      return;
    }

    const appUrls = getFormHostingLinksFromAppUrls(
      application.appUrls,
      appHostingLinks,
      'https://'
    );

    reset({ ...appSubmissionState, ...appUrls });
    setAppUrlChoices(appUrls);
  }, [application]);

  if (
    !application ||
    !currentOrg ||
    !currentTenant ||
    currentTenant.orgId != currentOrg.id
  ) {
    return null;
  }

  const updateIsMultiTenant = (setting: boolean) => {
    setIsMultiTenant(setting);
  };

  const updateHonorsCopyright = (setting: boolean) => {
    setHonorsCopyright(setting);
  };

  const onSubmit = handleSubmit(async () => {
    if (!currentTeam || !applicationId) {
      return;
    }

    setIsSubmitting(true);

    try {
      await patchApplicationDetails(
        currentOrg.id,
        currentTeam.id,
        applicationId,
        appSubmissionState.versionId,
        { ...appSubmissionState, isMultiTenant, honorsCopyright }
      );

      await updateApplicationLifecycle(
        currentOrg.id,
        currentTeam.id,
        applicationId,
        appSubmissionState.versionId,
        AppStatusCommand.SUBMIT_FOR_TECH_REVIEW
      );
    } catch (e) {
      navigate(`/application/${applicationId}/publish/result?status=error`, {
        replace: false,
      });
    }

    navigate(`/application/${applicationId}/publish/result?status=success`, {
      replace: false,
    });
  });

  if (isSubmitting) {
    return <FullLoadingIndicator hidden={false} underHeader={true} />;
  }

  return (
    <Page className="flex-col items-center">
      <Container
        className="mb-os1-spacing flex flex-col justify-start gap-6"
        size="medium"
      >
        <Form
          onSubmit={onSubmit}
          title={
            <div className="flex flex-row items-center gap-x-3">
              <PageTitle>{application.listingName}</PageTitle>
            </div>
          }
        >
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="listingName"
              placeholder={application.listingName}
              register={register}
              testId="listingName-input"
              title="App Name"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="urlPath"
              placeholder={application.urlPath}
              register={register}
              testId="urlPath-input"
              title="App Path URL"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="appType"
              placeholder={application.appType}
              register={register}
              testId="appType-input"
              title="App Type"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3 mb-4">
            <span className="max-w-xs pb-1 font-medium">Categories</span>
            <span className="flex flex-row items-center gap-x-3">
              {application.categories &&
                application.categories.map((cat, i) => (
                  <span key={i}>
                    <Badge>{cat}</Badge>
                  </span>
                ))}
            </span>
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="shortDescription"
              placeholder={application.shortDescription}
              register={register}
              testId="shortDescription-input"
              title="Description"
            />
          </div>
          <div className="flex flex-col w-full items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="version"
              placeholder={application.version}
              register={register}
              testId="version-input"
              title="Version"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3 mb-4">
            <span className="max-w-xs pb-1 font-medium">Icon</span>
            <div className="h-20 w-20 flex justify-center p-4 transition bg-white border-2 border-gray-300 border-solid rounded-md appearance-none">
              <img alt="App Icon" className="object-scale-down" src={iconUrl} />
            </div>
          </div>
          <div className="flex flex-col items-left gap-x-3 mb-4">
            <span className="max-w-xs pb-1 font-medium">Hosting location</span>
            <span className="flex flex-row items-center gap-x-3">
              <Badge>{application.hostingLocation}</Badge>
            </span>
          </div>
          <div className="flex flex-col items-left gap-x-3">
            {appUrlChoices.gitRepo && (
              <InputField
                disabled={true}
                errors={errors}
                inputKey="gitRepo"
                placeholder={appUrlChoices.gitRepo}
                register={register}
                required={false}
                testId="gitRepo-input"
                title="Git Link"
              />
            )}
            {appUrlChoices.serverUrl && (
              <InputField
                disabled={true}
                errors={errors}
                inputKey="serverUrl"
                placeholder={appUrlChoices.serverUrl}
                register={register}
                required={false}
                testId="serverUrl-input"
                title="Server URL"
              />
            )}
            {appUrlChoices.uiHomeUrl && (
              <InputField
                disabled={true}
                errors={errors}
                inputKey="uiHomeUrl"
                placeholder={appUrlChoices.uiHomeUrl}
                register={register}
                required={false}
                testId="uiHomeUrl-input"
                title="UI Home URL"
              />
            )}
            {appUrlChoices.userGuideUrl && (
              <InputField
                disabled={true}
                errors={errors}
                inputKey="userGuideUrl"
                placeholder={appUrlChoices.userGuideUrl}
                register={register}
                required={false}
                testId="userGuideUrl-input"
                title="User Guide URL"
              />
            )}
          </div>
          {application.appType == AppType.web && (
            <div className="flex flex-row justify-left gap-x-3">
              <InputItem title="Console Compatible" type="checkbox">
                <div className="flex items-center">
                  <input
                    disabled={true}
                    id="field-console-compatible"
                    type="checkbox"
                    {...getTextInputProps({
                      inputKey: 'consoleCompatible',
                      errors,
                      register,
                      className: 'border-gray-400 rounded-sm mr-3 mt-1',
                      type: 'checkbox',
                      required: false,
                    })}
                  />
                  <span className="text-xs">
                    App is compatible with the console app launch experience.
                  </span>
                </div>
              </InputItem>
            </div>
          )}
          <hr />
          <div className="flex flex-col mt-4 mb-10 gap-x-3">
            <span className="max-w-xs pb-1 font-medium">Agreements</span>
            <span className="mt-2 mb-6 text-xs">
              Please agree to the following before submission.
            </span>
            <fieldset className="mb-2">
              <div className="form-check" id="isMultiTenant">
                <input
                  className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                  id="isMultiTenant"
                  type="checkbox"
                  {...register('isMultiTenant', {
                    required: true,
                    onChange: (e) => updateIsMultiTenant(e.target.checked),
                  })}
                />
                <label
                  className="form-check-label inline-block text-gray-800"
                  htmlFor="isMultiTenant"
                >
                  This app is multi-tenant.
                </label>
              </div>
              {errors.isMultiTenant && (
                <div className="text-xs mt-2 text-red-600 dark:text-red-400 left-0">
                  Confirm acceptance.
                </div>
              )}
            </fieldset>
            <fieldset>
              <div className="form-check" id="honorsCopyright">
                <input
                  className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                  id="honorsCopyright"
                  type="checkbox"
                  {...register('honorsCopyright', {
                    required: true,
                    onChange: (e) => updateHonorsCopyright(e.target.checked),
                  })}
                />
                <label
                  className="form-check-label inline-block text-gray-800"
                  htmlFor="honorsCopyright"
                >
                  This app is free of IP or Copyright Infringements.
                </label>
              </div>
              {errors.honorsCopyright && (
                <div className="text-xs mt-2 text-red-600 dark:text-red-400 left-0">
                  Confirm acceptance.
                </div>
              )}
            </fieldset>
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <Button
              className="h-10"
              disabled={!isMultiTenant || !honorsCopyright}
              onClick={onSubmit}
              size="large"
            >
              Submit
            </Button>
          </div>
        </Form>
      </Container>
    </Page>
  );
};
